<template>
  <section class="mb-8">
    <div v-if="musicians.length" :class="{ 'pb-6 mb-4': audioWorks.length || substitute || showIntermission }">
      <h3 class="inline-block w-[25%] lg:w-1/5 align-top text-app-secondary-9-green--sherpa-blue text-lg xl:text-xl leading-none font-semibold mb-2 mr-1">
        {{ $t('program-musicians-title') }}
      </h3>
      <div class="inline-block w-3/5">
        <SmoothReflow class="overflow-hidden transition-all duration-500 ease-in-out">
          <ul class="text-sm md:text-base" :class="{ 'h-auto': showAllMusicians, 'h-24': !showAllMusicians && Object.keys(normalizedMusicians).length > 4 }">
            <li
              v-for="(musicianObj, index) in normalizedMusicians"
              :key="musicianObj.musician"
              class="leading-5 mb-1"
              :class="{
                'mb-0': Number(index) === Object.keys(normalizedMusicians).length - 1,
              }"
            >
              <div class="w-full">
                <strong class="font-semibold">{{ musicianObj.musician }}</strong>
                <template v-if="musicianObj.instruments.length > 0">{{ ' ' }}{{ musicianObj.instruments.join(', ') }}</template>
                <template v-if="musicianObj.roles.length > 0">{{ ' ' }}({{ musicianObj.roles.join(', ') }})</template>
              </div>
            </li>
          </ul>
        </SmoothReflow>
        <button v-if="Object.keys(normalizedMusicians).length > 4" class="underline inline-block mt-2" @click="toggleShowAllMusicians">
          {{ showAllMusicians ? t_('hide-all-musicians') : t_('show-all-musicians') }}
          <AtomIconChevronDown
            v-if="Object.keys(normalizedMusicians).length > 4"
            class="ml-2 transform transition duration-200 inline-block w-4 h-4"
            :class="{ 'rotate-180': showAllMusicians }"
          />
        </button>
      </div>
    </div>

    <div v-if="audioWorks.length || substitute" :class="{ 'mb-5': showIntermission }">
      <h3 class="inline-block w-[25%] lg:w-1/5 align-top text-app-secondary-9-green--sherpa-blue text-lg xl:text-xl leading-none font-semibold mb-4 mr-1">
        {{ $t('program-works-title') }}
      </h3>
      <MoleculeAudioWorksV2
        v-if="audioWorks.length && !substitute"
        class="inline-block w-3/5 lg:w-[70%]"
        :audio-works="audioWorks"
        :header-audio-is-playing="headerAudioIsPlaying"
        :toggle-header-audio="toggleHeaderAudio"
      />
      <div v-if="substitute" class="prose prose--app font-semibold text-sm mb-6" v-html="substitute" />
    </div>

    <!-- Concert program download button -->
    <MoleculeButton
      v-if="program"
      class="mb-10"
      size="medium"
      :new-window="true"
      :url="program.url"
      :background-color="theme ? `t-cdp-button-bg-secondary-${theme}` : 'bg-app-secondary-8-green--vista-blue'"
      :text-color="theme ? `t-cdp-button-text-secondary-${theme}` : 'text-app-primary-1-red--torch'"
    >
      {{ t_('program') }}
    </MoleculeButton>

    <ul v-if="showIntermission || isAmplifiedConcert" class="mb-8">
      <template v-if="showIntermission">
        <li v-for="(item, index) in items" :key="index" :class="{ 'mb-1': index + 1 === items.length }">
          <MoleculeInfoPanel class="border-t-0 border-b-0 pt-0 pb-0" text-color="text-app-secondary-9-green--sherpa-blue">{{ item.description }}</MoleculeInfoPanel>
        </li>
      </template>
      <li v-if="isAmplifiedConcert" class="mb-1">
        <MoleculeInfoPanel class="border-t-0 border-b-0 pt-0 pb-0" text-color="text-app-secondary-9-green--sherpa-blue" :hide-exclamation-mark="showIntermission">
          {{ amplifiedConcert.description }}
          <a v-if="amplifiedConcert.url" :href="amplifiedConcert.url">{{ amplifiedConcert.label }}</a>
        </MoleculeInfoPanel>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import groupBy from 'lodash/groupBy'
import mapValues from 'lodash/mapValues'
import uniq from 'lodash/uniq'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  audioWorks: { type: Array as PropType<any[]>, required: false, default: () => [] },
  hasIntermission: { type: Boolean, required: false, default: false },
  intermissionDescription: { type: String, required: false, default: '' },
  isAmplifiedConcert: { type: Boolean, required: false, default: false },
  musicians: { type: Array as PropType<any[]>, required: false, default: () => [] },
  showIntermission: { type: Boolean, required: false, default: true },
  substitute: { type: String, required: false, default: '' },
  headerAudioIsPlaying: { type: Boolean, required: true },
  toggleHeaderAudio: { type: Function, required: true },
  program: { type: Object as PropType<Record<string, any>>, required: false, default: undefined },
  theme: { type: String, required: false, default: undefined },
})

const { t_ } = setupTranslationPrefixer('program')
const showAllMusicians = ref(false)
const gtmCdpAction = setupGtmCdpAction()

const { t } = useI18n()
const items = ref([
  {
    label: t('concert.detail.has-intermission-title'),
    description:
      props.intermissionDescription ||
      (props.hasIntermission ? t('concert.detail.has-intermission') : props.showIntermission ? t('concert.detail.has-no-intermission') : undefined),
  },
])
const amplifiedConcert = {
  description: t('concert.detail.is-amplified-concert'),
  url: t('concert.detail.is-amplified-concert-button-url'),
  label: t('concert.detail.is-amplified-concert-button-label'),
}
const normalizedMusicians = computed(() =>
  mapValues(
    groupBy(props.musicians, (musician) => musician.title),
    (musicians) => ({
      musician: musicians[0].title,
      instruments: uniq(musicians.map((musician) => musician.instrumentDescription).filter((value) => !!value)),
      roles: uniq(musicians.map((musician) => musician.role).filter((value) => !!value)),
    }),
  ),
)

const toggleShowAllMusicians = () => {
  showAllMusicians.value = !showAllMusicians.value
  if (showAllMusicians.value) {
    gtmCdpAction('CV07 - See all musici clicked')
  }
}
</script>

<style lang="scss" scoped>
.c-program-musicians {
  @screen md {
    column-count: 2;
  }
}
</style>
